import { Injectable } from '@angular/core';

export interface ErrorFormatOptions {
    format?: 'text' | 'html';
    separator?: string;
    fieldWrapper?: {
        start: string;
        end: string;
    };
}

interface ParsedError {
    error?: {
        errors?: Record<string, string[]>;
        message?: string;
    } | string;
    message?: string;
}

/**
 * Shared utility service for handling error message extraction and formatting.
 * Centralizes error parsing logic to eliminate code duplication across the application.
 */
@Injectable({
    providedIn: 'root',
})
export class ErrorUtilService {

    /**
     * Extract and format error message from API response
     * @param error The error object from API response
     * @param options Formatting options for output
     * @returns Formatted error message string
     */
    extractErrorMessage (error: unknown, options: ErrorFormatOptions = {}): string {
        const {
            format = 'text',
            separator = format === 'html' ? '<br />' : '; ',
            fieldWrapper = format === 'html'
                ? { start: '<strong>', end: '</strong>' }
                : { start: '', end: '' },
        } = options;

        let errorMessage = 'Unknown error';
        const err = error as ParsedError;

        if (err?.error && typeof err.error === 'object' && 'errors' in err.error && err.error.errors) {
            const errors = err.error.errors;
            const errorMessages: string[] = [];

            for (const field in errors) {
                if (errors[field] && Array.isArray(errors[field])) {
                    const fieldName = field.replace(/\[|\]/g, ''); // Clean field name
                    const params = errors[field].join(', ');
                    const formattedField = `${fieldWrapper.start}${fieldName}${fieldWrapper.end}`;
                    errorMessages.push(`${formattedField}: ${params}`);
                }
            }

            if (errorMessages.length > 0) {
                errorMessage = errorMessages.join(separator);
            }
        } else if (err?.error && typeof err.error === 'object' && 'message' in err.error) {
            errorMessage = err.error.message || 'Unknown error';
        } else if (err?.error && typeof err.error === 'string') {
            // Handle HTML error messages by removing tags for text format
            errorMessage = format === 'text'
                ? err.error
                    .replace(/<[^>]*>/g, '') // Remove HTML tags
                    .replace(/\s+/g, ' ') // Normalize whitespace
                    .trim()
                : err.error;
        } else if (err?.message) {
            errorMessage = err.message;
        }

        return errorMessage;
    }

    /**
     * Extract error message with text formatting (for programmatic use)
     * @param error The error object from API response
     * @returns Plain text error message
     */
    extractTextErrorMessage (error: unknown): string {
        return this.extractErrorMessage(error, { format: 'text' });
    }

    /**
     * Extract error message with HTML formatting (for UI alerts)
     * @param error The error object from API response
     * @returns HTML-formatted error message
     */
    extractHtmlErrorMessage (error: unknown): string {
        return this.extractErrorMessage(error, { format: 'html' });
    }
}
